/**
 * The first run shows for the LaunchDarkly flag or for the dev override, and
 * for nothing else: an absent flag is off. The override is `?firstRun=1`,
 * kept in localStorage until `?firstRun=0`, and honoured in dev builds only,
 * the same guard as `TourReset`.
 *
 * The param is read once, when this module loads at app boot, not when the
 * shell mounts: a logged-out visit to `/?firstRun=1` leaves for Auth0 before
 * the shell exists and comes back without the query string, so a read at
 * mount would never see it.
 */

import type { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";

export const OVERRIDE_KEY = "co-first-run-override";

/** Applies `?firstRun=1` / `?firstRun=0` to storage and reads what is stored. */
export function readFirstRunOverride(
  search: string = window.location.search,
): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    const param = new URLSearchParams(search).get("firstRun");
    if (param === "1") window.localStorage.setItem(OVERRIDE_KEY, "1");
    else if (param === "0") window.localStorage.removeItem(OVERRIDE_KEY);
    return window.localStorage.getItem(OVERRIDE_KEY) === "1";
  } catch {
    return false;
  }
}

const override = readFirstRunOverride();

/** The dev override, as it stood when the app booted. */
export function isFirstRunOverridden(): boolean {
  return override;
}

export function useFirstRunEnabled(): boolean {
  const flag = !!useFlags<Flags>().firstRun;
  return flag || override;
}
